import { ChatCircleText, NotePencil, WarningCircle } from "@phosphor-icons/react";
import { StarRating } from "@/components/reviews/StarRating";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/therapist/settings/section-primitives";
import { formatDate } from "@/lib/format";
import { REVIEW_REPLY_MAX } from "@/lib/review-limits";
import { reviewStatusLabel } from "@/lib/review-status-label";
import { cn } from "@/lib/utils";
import type { SettingsStatusTone } from "@/lib/therapist-settings-status";
import type { TherapistReview } from "@/types";

export function ReviewStat({
  icon: Icon,
  iconClass,
  label,
  value,
  hint,
  accent = false,
}: {
  icon: typeof ChatCircleText;
  iconClass?: string;
  label: string;
  value: string;
  hint: string;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-border/60 bg-card px-4 py-3 shadow-sm",
        accent && "border-amber-200 bg-amber-50/60",
      )}
    >
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Icon className={cn("size-4", accent ? "text-amber-700" : iconClass)} />
        <span>{label}</span>
      </div>
      <p className="mt-1.5 text-2xl font-bold tabular-nums text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
        {value}
      </p>
      <p className="truncate text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

const REVIEW_STATUS_TONE: Record<TherapistReview["status"], SettingsStatusTone> = {
  pending: "warning",
  approved: "ready",
  rejected: "blocked",
  hidden: "neutral",
};

export function ReviewCard({
  review,
  isEditing,
  replyText,
  onEdit,
  onReply,
  onReplyTextChange,
  onCancel,
  onSave,
  isSaving = false,
}: {
  review: TherapistReview;
  isEditing: boolean;
  replyText: string;
  onEdit: () => void;
  onReply: () => void;
  onReplyTextChange: (value: string) => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving?: boolean;
}) {
  return (
    <div className="border-b border-border/60 py-5 first:pt-0.5 last:border-b-0 last:pb-0.5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={REVIEW_STATUS_TONE[review.status]} label={reviewStatusLabel[review.status]} />
          <span className="text-xs text-muted-foreground">{formatDate(review.createdAt)}</span>
        </div>
        <StarRating value={review.rating} readOnly size="sm" />
      </div>

      <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{review.reviewText}</p>

      {review.status === "rejected" && review.rejectionReason ? (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50/60 p-3 text-sm text-rose-900">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-rose-700">
            <WarningCircle className="size-3.5" weight="fill" />
            Razlog odbijanja
          </div>
          <p className="whitespace-pre-wrap leading-relaxed">{review.rejectionReason}</p>
        </div>
      ) : null}

      {review.therapistReply ? (
        <div className="mt-4 rounded-xl border border-primary/10 bg-primary/5 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <ChatCircleText className="size-4 text-primary" weight="duotone" />
              Vaš odgovor
            </div>
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <NotePencil className="size-3.5" />
              Uredi
            </Button>
          </div>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
            {review.therapistReply}
          </p>
        </div>
      ) : (
        <Button className="mt-4" variant="outline" size="sm" onClick={onReply}>
          <ChatCircleText className="size-3.5" />
          Odgovori
        </Button>
      )}

      {isEditing ? (
        <div className="mt-4 rounded-xl border border-border/60 bg-muted/30 p-4">
          <label className="text-sm font-medium text-foreground">Odgovor terapeuta</label>
          <textarea
            className="mt-2 min-h-28 w-full resize-y rounded-lg border border-border bg-background p-3 text-sm leading-relaxed outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
            maxLength={REVIEW_REPLY_MAX}
            value={replyText}
            onChange={(event) => onReplyTextChange(event.target.value)}
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">{replyText.length}/{REVIEW_REPLY_MAX}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onCancel}>Otkaži</Button>
              <Button size="sm" onClick={onSave} disabled={!replyText.trim() || isSaving}>
                {isSaving ? "Spremam..." : "Sačuvaj"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function ReviewSkeleton() {
  return (
    <div>
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="border-b border-border/60 py-5 first:pt-0.5 last:border-b-0">
          <div className="flex items-center justify-between">
            <div className="h-5 w-28 animate-pulse rounded-full bg-muted" />
            <div className="h-5 w-24 animate-pulse rounded bg-muted" />
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <div className="h-3 w-full animate-pulse rounded bg-muted" />
            <div className="h-3 w-4/5 animate-pulse rounded bg-muted" />
          </div>
          <div className="mt-4 h-9 w-32 animate-pulse rounded-lg bg-muted" />
        </div>
      ))}
    </div>
  );
}

export function EmptyState() {
  return (
    <div className="py-12 text-center">
      <h3 className="text-base font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
        Još nemate recenzija
      </h3>
      <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-muted-foreground">
        Kada klijenti ostave recenziju, ovdje ćete vidjeti ocjenu, status pregleda i opciju za odgovor.
      </p>
    </div>
  );
}
